import React, { useState, useMemo, useCallback } from 'react';
import {
  ArrowLeft, Flame, TrendingUp, Infinity, CheckCircle2,
  Target, Zap, BarChart3, RefreshCw,
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { useAppState } from '../store/AppContext';
import { getAssetCurrentValue, getAssetAnnualIncome } from '../utils/calculations';
import { formatVND, formatVNDAxis } from '../utils/format';
import { Bucket } from '../types';

// ── FIRE Math Engine ──────────────────────────────────────────────────────────
/**
 * PV của dòng tiền rút vốn tăng theo lạm phát (Growing Annuity PV)
 * C = chi tiêu năm đầu (triệu VND)
 * r = lợi nhuận thực (decimal, vd 0.08)
 * g = lạm phát (decimal, vd 0.04)
 * n = số năm
 */
function growingAnnuityPV(C: number, r: number, g: number, n: number): number {
  if (Math.abs(r - g) < 0.0001) return C * n / (1 + r); // r ≈ g
  return C * (1 - Math.pow((1 + g) / (1 + r), n)) / (r - g);
}

interface FireScenarios {
  min: number;         // Tối thiểu: rút hết về 0
  recommended: number; // Khuyến nghị: còn 30% thực sau horizon
  perpetual: number;   // Vĩnh viễn: endowment không bao giờ cạn
}

function calcFire(spending: number, returnPct: number, inflationPct: number, horizon: number): FireScenarios {
  const r = returnPct   / 100;
  const g = inflationPct / 100;
  const C = spending; // triệu VND / năm

  const pvMin = growingAnnuityPV(C, r, g, horizon);

  // 30% còn lại tính theo giá trị thực (inflation-adjusted)
  const alpha = Math.pow((1 + g) / (1 + r), horizon);
  const denom = 1 - 0.3 * alpha;
  const pvRec = denom > 0.01 ? pvMin / denom : pvMin * 1.5;

  // Endowment: C / (r - g) — corpus tăng trưởng mãi, rút vĩnh viễn
  const pvPerp = r > g + 0.001 ? C / (r - g) : C / 0.04;

  return { min: pvMin, recommended: pvRec, perpetual: pvPerp };
}

/** Chiếu tăng trưởng danh mục theo năm để vẽ biểu đồ */
function projectPortfolio(
  startNav: number,
  targetNav: number,
  spending: number,
  returnPct: number,
  inflationPct: number,
  horizon: number,
): { year: number; actual: number; target: number; withdrawal: number }[] {
  const r = returnPct / 100;
  const g = inflationPct / 100;
  const rows = [];
  let nav = startNav;
  for (let t = 0; t <= horizon; t++) {
    const w = t === 0 ? 0 : spending * Math.pow(1 + g, t - 1);
    rows.push({ year: t, actual: Math.max(0, nav), target: targetNav * Math.pow(1 + g, t), withdrawal: w });
    nav = nav * (1 + r) - w;
  }
  return rows;
}

// ── Slider component ──────────────────────────────────────────────────────────
function Slider({
  label, value, min, max, step, unit, format, color = '#10b981', onChange,
}: {
  label: string; value: number; min: number; max: number; step: number;
  unit: string; color?: string; format?: (v: number) => string;
  onChange: (v: number) => void;
}) {
  const pct = ((value - min) / (max - min)) * 100;
  const display = format ? format(value) : `${value}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: 11, color: 'var(--tx-4)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
        <span style={{ fontSize: 16, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>
          {display}<span style={{ fontSize: 11, fontWeight: 400, color: 'var(--tx-4)', marginLeft: 3 }}>{unit}</span>
        </span>
      </div>
      <div style={{ position: 'relative', height: 20, display: 'flex', alignItems: 'center' }}>
        {/* Track */}
        <div style={{ position: 'absolute', left: 0, right: 0, height: 4, borderRadius: 2, background: 'var(--bd)' }} />
        {/* Fill */}
        <div style={{ position: 'absolute', left: 0, width: `${pct}%`, height: 4, borderRadius: 2, background: `linear-gradient(90deg, ${color}88, ${color})`, transition: 'width 0.05s' }} />
        {/* Thumb */}
        <input
          type="range" min={min} max={max} step={step} value={value}
          onChange={e => onChange(Number(e.target.value))}
          style={{
            position: 'absolute', left: 0, right: 0, width: '100%',
            opacity: 0, cursor: 'pointer', height: 20, margin: 0,
          }}
        />
        <div style={{
          position: 'absolute', left: `calc(${pct}% - 8px)`,
          width: 16, height: 16, borderRadius: '50%',
          background: color, border: '2px solid var(--bg-card)',
          boxShadow: `0 0 0 2px ${color}44, 0 2px 6px rgba(0,0,0,0.4)`,
          pointerEvents: 'none', transition: 'left 0.05s',
        }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--tx-5)' }}>
        <span>{format ? format(min) : min}{unit}</span>
        <span>{format ? format(max) : max}{unit}</span>
      </div>
    </div>
  );
}

// ── Scenario Card ──────────────────────────────────────────────────────────────
function ScenarioCard({
  icon, label, subtitle, value, selected, color, onSelect,
}: {
  icon: React.ReactNode; label: string; subtitle: string;
  value: number; selected: boolean; color: string;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      style={{
        flex: 1, minWidth: 0, padding: '16px', borderRadius: 14, cursor: 'pointer', textAlign: 'left',
        border: `1.5px solid ${selected ? color : 'var(--bd)'}`,
        background: selected ? `${color}0f` : 'var(--bg-card-el)',
        boxShadow: selected ? `0 0 0 1px ${color}40, 0 4px 16px ${color}1a` : 'none',
        transition: 'all 0.18s',
        outline: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 8,
          background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {React.cloneElement(icon as React.ReactElement<any>, { size: 13, color })}
        </div>
        {selected && <CheckCircle2 size={14} color={color} />}
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, color: selected ? color : 'var(--tx-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ fontSize: 10, color: 'var(--tx-5)', marginBottom: 10, lineHeight: 1.4 }}>{subtitle}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: selected ? color : 'var(--tx-1)', letterSpacing: '-0.03em', lineHeight: 1 }}>
        {formatVND(value * 1_000_000)}
      </div>
    </button>
  );
}

// ── Tooltip tùy chỉnh ─────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--bd)', borderRadius: 10, padding: '10px 14px', fontSize: 11 }}>
      <div style={{ fontWeight: 700, color: 'var(--tx-3)', marginBottom: 6 }}>Năm {label}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2 }}>
          <div style={{ width: 8, height: 8, borderRadius: 2, background: p.color }} />
          <span style={{ color: 'var(--tx-4)' }}>{p.name}:</span>
          <span style={{ color: p.color, fontWeight: 600 }}>
            {formatVND(p.value * 1_000_000)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export function FundFireCalculator({ fundId, onBack }: { fundId: string; onBack: () => void }) {
  const { state, dispatch } = useAppState();
  const bucket = state.buckets.find(b => b.id === fundId);
  const fundAssets = state.assets.filter(a => a.bucketId === fundId);

  // NAV thực tế (triệu VND)
  const actualNAV = fundAssets.reduce((s, a) => s + getAssetCurrentValue(a), 0) / 1_000_000;
  const annualIncome = fundAssets.reduce((s, a) => s + getAssetAnnualIncome(a), 0) / 1_000_000;
  const totalCost = fundAssets.reduce((s, a) => s + (a.costPrice ?? 0) * (a.quantity ?? 1), 0) / 1_000_000;
  const gainPct = totalCost > 0 ? ((actualNAV - totalCost) / totalCost) * 100 : 0;
  const yieldPct = actualNAV > 0 ? (annualIncome / actualNAV) * 100 : 0;

  // Slider state — khởi tạo từ giá trị đã lưu trong bucket
  const [spending,   setSpending]   = useState(bucket?.spendingPerYear ?? 600);
  const [retRate,    setRetRate]    = useState(bucket?.expectedReturn  ?? 8);
  const [inflation,  setInflation]  = useState(bucket?.inflationRate   ?? 4);
  const [horizon,    setHorizon]    = useState(bucket?.horizonYears    ?? 30);
  const [selected,   setSelected]   = useState<'min' | 'recommended' | 'perpetual'>(bucket?.strategyType ?? 'recommended');
  const [saving,     setSaving]     = useState(false);
  const [saved,      setSaved]      = useState(false);

  // Real-time tính kịch bản
  const scenarios = useMemo(
    () => calcFire(spending, retRate, inflation, horizon),
    [spending, retRate, inflation, horizon],
  );

  const targetNAV = scenarios[selected]; // triệu VND

  // Projection chart data
  const chartData = useMemo(
    () => projectPortfolio(actualNAV, targetNAV, spending, retRate, inflation, horizon),
    [actualNAV, targetNAV, spending, retRate, inflation, horizon],
  );

  const completionPct = targetNAV > 0 ? Math.min((actualNAV / targetNAV) * 100, 150) : 0;
  const gap = targetNAV - actualNAV; // triệu VND

  const handleApply = useCallback(async () => {
    if (!bucket) return;
    setSaving(true);
    const updated: Bucket = {
      ...bucket,
      targetValue:    Math.round(targetNAV * 1_000_000),
      spendingPerYear: spending,
      expectedReturn:  retRate,
      inflationRate:   inflation,
      horizonYears:    horizon,
      strategyType:    selected,
    };
    dispatch({ type: 'UPDATE_BUCKET', payload: updated });
    await new Promise(r => setTimeout(r, 800)); // chờ debounce save
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }, [bucket, targetNAV, spending, retRate, inflation, horizon, selected, dispatch]);

  if (!bucket) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-4)' }}>
      Không tìm thấy quỹ.
    </div>
  );

  const accentColor = bucket.color || '#10b981';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <style>{`
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes slideIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        input[type=range]::-webkit-slider-thumb { -webkit-appearance:none; width:0; height:0; }
        input[type=range]::-moz-range-thumb { width:0; height:0; border:none; background:none; }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <button
          onClick={onBack}
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--tx-4)', background: 'none', border: '1px solid var(--bd)', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}
        >
          <ArrowLeft size={13} /> Quay lại
        </button>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: `linear-gradient(135deg, ${accentColor}30, ${accentColor}15)`,
          border: `1px solid ${accentColor}30`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Flame size={16} style={{ color: accentColor }} />
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--tx-1)' }}>{bucket.name}</div>
          <div style={{ fontSize: 11, color: 'var(--tx-4)' }}>Mô phỏng chiến lược FIRE</div>
        </div>
      </div>

      {/* 2-col layout: Bộ tính | Hiệu suất */}
      <div className="rsp-grid-2" style={{ gap: 16, alignItems: 'start' }}>

        {/* ── Bộ tính Sliders ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ padding: '20px 22px', borderRadius: 16, background: 'var(--bg-card)', border: '1px solid var(--bd)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 18 }}>
              Tham số mô phỏng
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <Slider
                label="Chi tiêu / năm"
                value={spending} min={100} max={5000} step={50}
                unit="tr/năm" color="#38bdf8"
                format={v => formatVND(v * 1_000_000)}
                onChange={setSpending}
              />
              <Slider
                label="Lợi nhuận danh mục"
                value={retRate} min={3} max={20} step={0.5}
                unit="%" color="#10b981"
                onChange={setRetRate}
              />
              <Slider
                label="Lạm phát / năm"
                value={inflation} min={1} max={10} step={0.5}
                unit="%" color="#fbbf24"
                onChange={setInflation}
              />
              <Slider
                label="Horizon (số năm)"
                value={horizon} min={10} max={50} step={1}
                unit=" năm" color="#a78bfa"
                onChange={setHorizon}
              />
            </div>

            {/* Real return indicator */}
            <div style={{ marginTop: 16, padding: '8px 12px', borderRadius: 8, background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.15)', display: 'flex', gap: 16 }}>
              <div>
                <div style={{ fontSize: 10, color: 'var(--tx-5)' }}>Lợi nhuận thực (sau lạm phát)</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: retRate > inflation ? '#10b981' : '#f87171' }}>
                  {(retRate - inflation).toFixed(1)}% / năm
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: 'var(--tx-5)' }}>Tổng chi tiêu {horizon} năm</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tx-2)' }}>
                  {formatVND(spending * horizon * 1_000_000)}
                </div>
              </div>
            </div>
          </div>

          {/* 3 Scenario Cards */}
          <div style={{ display: 'flex', gap: 10 }}>
            <ScenarioCard
              icon={<Zap />} label="Tối thiểu" color="#38bdf8"
              subtitle={`Rút hết về 0 sau ${horizon} năm`}
              value={scenarios.min}
              selected={selected === 'min'}
              onSelect={() => setSelected('min')}
            />
            <ScenarioCard
              icon={<TrendingUp />} label="Khuyến nghị" color="#10b981"
              subtitle={`Còn lại ~30% sau ${horizon} năm`}
              value={scenarios.recommended}
              selected={selected === 'recommended'}
              onSelect={() => setSelected('recommended')}
            />
            <ScenarioCard
              icon={<Infinity />} label="Vĩnh viễn" color="#a78bfa"
              subtitle="Endowment — corpus không bao giờ cạn"
              value={scenarios.perpetual}
              selected={selected === 'perpetual'}
              onSelect={() => setSelected('perpetual')}
            />
          </div>

          {/* Apply button */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={handleApply}
              disabled={saving}
              style={{
                flex: 1, padding: '12px 20px', borderRadius: 12, fontWeight: 700, fontSize: 13,
                cursor: saving ? 'wait' : 'pointer', border: 'none',
                background: saved
                  ? 'linear-gradient(135deg, #059669, #10b981)'
                  : `linear-gradient(135deg, ${accentColor}dd, ${accentColor})`,
                color: '#fff',
                boxShadow: `0 4px 14px ${accentColor}40`,
                transition: 'all 0.2s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              {saving && <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} />}
              {saved  && <CheckCircle2 size={13} />}
              {saving ? 'Đang lưu chiến lược...' : saved ? 'Đã lưu thành công!' : `Áp dụng kịch bản ${selected === 'min' ? 'Tối thiểu' : selected === 'recommended' ? 'Khuyến nghị' : 'Vĩnh viễn'}`}
            </button>
            <div style={{ fontSize: 10, color: 'var(--tx-5)', maxWidth: 180, lineHeight: 1.4 }}>
              Sẽ cập nhật target_value = {formatVND(targetNAV * 1_000_000)} và đóng băng tham số chiến lược vào DB.
            </div>
          </div>
        </div>

        {/* ── Dashboard Hiệu suất ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* NAV so sánh */}
          <div style={{ padding: '18px 20px', borderRadius: 16, background: 'var(--bg-card)', border: '1px solid var(--bd)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>
              Hiệu suất Quỹ
            </div>

            {/* NAV thực tế */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: 'var(--tx-5)', marginBottom: 3 }}>NAV Thực tế</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--tx-1)', letterSpacing: '-0.03em' }}>
                {formatVND(actualNAV * 1_000_000)}
              </div>
              <div style={{ fontSize: 11, color: gainPct >= 0 ? '#10b981' : '#f87171', marginTop: 2 }}>
                {gainPct >= 0 ? '+' : ''}{gainPct.toFixed(1)}% lãi/lỗ vốn
              </div>
            </div>

            {/* NAV mục tiêu */}
            <div style={{ marginBottom: 14, padding: '10px 12px', borderRadius: 10, background: `${accentColor}0a`, border: `1px solid ${accentColor}20` }}>
              <div style={{ fontSize: 10, color: 'var(--tx-5)', marginBottom: 3 }}>
                NAV Mục tiêu ({selected === 'min' ? 'Tối thiểu' : selected === 'recommended' ? 'Khuyến nghị' : 'Vĩnh viễn'})
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: accentColor, letterSpacing: '-0.02em' }}>
                {formatVND(targetNAV * 1_000_000)}
              </div>
            </div>

            {/* Progress bar */}
            <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
              <span style={{ color: 'var(--tx-4)' }}>Hoàn thành</span>
              <span style={{ fontWeight: 700, color: completionPct >= 100 ? '#10b981' : accentColor }}>
                {completionPct.toFixed(1)}%
              </span>
            </div>
            <div style={{ height: 8, borderRadius: 4, background: 'var(--bd)', overflow: 'hidden', marginBottom: 8 }}>
              <div style={{
                height: '100%', borderRadius: 4,
                width: `${Math.min(completionPct, 100)}%`,
                background: completionPct >= 100
                  ? 'linear-gradient(90deg,#10b981,#059669)'
                  : `linear-gradient(90deg, ${accentColor}88, ${accentColor})`,
                transition: 'width 0.4s ease',
              }} />
            </div>

            {/* Gap */}
            <div style={{ fontSize: 11, color: 'var(--tx-4)' }}>
              {gap > 0
                ? <>Còn thiếu <strong style={{ color: '#fbbf24' }}>{formatVND(gap * 1_000_000)}</strong></>
                : <>Vượt mục tiêu <strong style={{ color: '#10b981' }}>{formatVND(Math.abs(gap) * 1_000_000)}</strong></>
              }
            </div>
          </div>

          {/* KPI cards */}
          <div className="rsp-grid-2" style={{ gap: 8 }}>
            {[
              { label: 'Thu nhập/năm', value: formatVND(annualIncome * 1_000_000), color: '#10b981', icon: <BarChart3 size={11} /> },
              { label: 'Yield hiện tại', value: `${yieldPct.toFixed(1)}%`, color: '#38bdf8', icon: <TrendingUp size={11} /> },
              { label: 'Số tài sản', value: `${fundAssets.length} tài sản`, color: 'var(--tx-3)', icon: <Target size={11} /> },
              { label: 'Chi tiêu/tháng', value: formatVND(spending * 1_000_000 / 12), color: '#fbbf24', icon: <Zap size={11} /> },
            ].map(k => (
              <div key={k.label} style={{ padding: '10px 12px', borderRadius: 10, background: 'var(--bg-card-el)', border: '1px solid var(--bd)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5, color: k.color }}>
                  {k.icon}
                  <span style={{ fontSize: 10, color: 'var(--tx-5)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{k.label}</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: k.color }}>{k.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Projection Chart */}
      <div style={{ padding: '20px 22px', borderRadius: 16, background: 'var(--bg-card)', border: '1px solid var(--bd)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Biểu đồ chiếu tăng trưởng
            </div>
            <div style={{ fontSize: 11, color: 'var(--tx-5)', marginTop: 2 }}>
              Danh mục thực tế vs mục tiêu qua {horizon} năm
            </div>
          </div>
          <div style={{ display: 'flex', gap: 14, fontSize: 11 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--tx-4)' }}>
              <span style={{ width: 10, height: 3, background: accentColor, borderRadius: 2, display: 'inline-block' }} />
              NAV thực tế
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--tx-4)' }}>
              <span style={{ width: 10, height: 3, background: '#fbbf24', borderRadius: 2, display: 'inline-block', borderTop: '1px dashed #fbbf24' }} />
              Mục tiêu
            </span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="gradActual" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={accentColor} stopOpacity={0.25} />
                <stop offset="95%" stopColor={accentColor} stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="gradTarget" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#fbbf24" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#fbbf24" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" strokeOpacity={0.4} />
            <XAxis dataKey="year" tick={{ fontSize: 10, fill: 'var(--tx-5)' }} tickFormatter={v => `N${v}`} />
            <YAxis tick={{ fontSize: 10, fill: 'var(--tx-5)' }} tickFormatter={v => formatVNDAxis(v * 1_000_000)} width={48} />
            <Tooltip content={<ChartTooltip />} />
            <Area
              type="monotone" dataKey="actual" name="NAV thực tế"
              stroke={accentColor} strokeWidth={2}
              fill="url(#gradActual)" dot={false}
            />
            <Area
              type="monotone" dataKey="target" name="Mục tiêu"
              stroke="#fbbf24" strokeWidth={1.5} strokeDasharray="5 3"
              fill="url(#gradTarget)" dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
